import React, { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { InferType, object } from 'yup';

import { CommandCenterClientProfile } from '@ndvr/customer-api';
import { CustodyType, PortfolioType, ReviewStatus } from '@ndvr/portfolio-api';

import { FormSheet } from '@/components/FormSheet';
import { getClientName, useRequiredParams } from 'utils';
import { useLinkPortfolio } from 'views/client/client-queries';
import { usePortfolios } from 'views/portfolio/portfolio-queries';
import { AccountSummary } from 'views/portfolio/PortfolioDetails/AccountSummary';
import {
    Card,
    custodyAccountIdValidator,
    FormInput,
    FormSection,
    FormSelect,
    normalizeCustodyAccountId,
    requiredStringValidator,
} from 'widgets';

const formSchema = object({
    custodyType: requiredStringValidator.oneOf(Object.values(CustodyType)),
    custodyAccount: custodyAccountIdValidator('custodyType'),
    portfolio: requiredStringValidator,
});

type FormType = InferType<typeof formSchema>;

export const LinkPortfolioDrawer = ({
    onClose,
    clientProfile,
}: {
    onClose: () => void;
    clientProfile: CommandCenterClientProfile;
}) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: linkClient, isPending: isLinking } = useLinkPortfolio(globalAccountGUID);
    const { data: portfolios } = usePortfolios(globalAccountGUID, PortfolioType.DRAFT, false);

    const onSubmit = useCallback(
        (data: FormType) => {
            const portfolio = portfolios?.find(({ portfolioGUID }) => portfolioGUID === data.portfolio);

            if (!portfolio) {
                return;
            }

            linkClient({
                clientGUID: clientProfile.client.clientGUID,
                portfolio,
                request: {
                    custodyType: data.custodyType,
                    custodyAccountId: data.custodyAccount,
                },
            });
        },
        [clientProfile.client.clientGUID, linkClient, portfolios],
    );

    return (
        <FormSheet
            size="lg"
            title={`Link Account to ${getClientName(clientProfile)}`}
            onClose={onClose}
            onSubmit={onSubmit}
            formSchema={formSchema}
            pending={isLinking}
            submitLabel="Link"
        >
            <FormSection>
                <FormSelect label="Custodian" name="custodyType">
                    <option value={CustodyType.FIDELITY}>Fidelity</option>
                    <option value={CustodyType.SCHWAB}>Charles Schwab</option>
                    <option value={CustodyType.IB}>Interactive Brokers</option>
                </FormSelect>
                <FormInput
                    label="Custody Account"
                    name="custodyAccount"
                    placeholder="account id"
                    normalize={normalizeCustodyAccountId}
                />
                <FormSelect label="Account" name="portfolio" description="You can only link draft accounts">
                    <option></option>
                    {portfolios
                        ?.sort((a) => (a.reviewStatus !== ReviewStatus.APPROVED ? 1 : -1))
                        .map((portfolio) => (
                            <option value={portfolio.portfolioGUID} key={portfolio.portfolioGUID}>
                                {portfolio.name.plainText}
                            </option>
                        ))}
                </FormSelect>
                <SelectedPortfolioSummary />
            </FormSection>
        </FormSheet>
    );
};

const SelectedPortfolioSummary = () => {
    const portfolioGUID = useWatch({ name: 'portfolio' });

    if (!portfolioGUID) {
        return null;
    }

    return (
        <Card $maxWidth="700px">
            <AccountSummary accountGUID={portfolioGUID} />
        </Card>
    );
};
